import { FC, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@contexts/AuthContext.tsx";
import { consumeReturnTo } from "@utils/auth-return-to";
import LoadingFallback from "../common/LoadingFallback";

interface PublicRouteProps {
  children: React.ReactNode;
  allowUnverified?: boolean; // Allow access even if email is not verified
}

const PublicRoute: FC<PublicRouteProps> = ({
  children,
  allowUnverified = false,
}) => {
  const { isAuthenticated, emailVerified, isLoading } = useAuth();
  // Resolve the post-login destination exactly once. consumeReturnTo() clears the
  // stored value, so guarding it behind a ref keeps a double render (StrictMode /
  // concurrent rendering) from consuming it twice and falling back to the default.
  const destinationRef = useRef<string | null>(null);

  if (isLoading) {
    return <LoadingFallback message="Checking authentication..." />;
  }

  // If user is authenticated and email is verified, return them to the URL
  // they originally requested (deep link), falling back to the default page
  if (isAuthenticated && emailVerified) {
    if (destinationRef.current === null) {
      destinationRef.current = consumeReturnTo() ?? "/surveys";
    }
    return <Navigate to={destinationRef.current} replace />;
  }

  // If user is authenticated but email is not verified
  if (isAuthenticated && !emailVerified) {
    // If this route allows unverified users (like verify-email page), show it
    if (allowUnverified) {
      return <>{children}</>;
    }
    // Otherwise redirect to verify email
    return <Navigate to="/verify-email" replace />;
  }

  // User is not authenticated, show the public route
  return <>{children}</>;
};

export default PublicRoute;
